'use client'

import { useState, useEffect, use, useCallback } from 'react'
import {
  cn,
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@anytimefitness/pb-react-ui'
import { Check, ChevronsUpDown, LayoutGrid } from 'lucide-react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import {
  useSelectedStudioStoreActions,
  useSelectedStudioStore,
} from '@/stores/selected-studio-store'
import { type Studio } from '@/types/studios'
import { formatStudioDisplayName } from '@/utils/studios'

export default function StudioSelectCombobox({
  studiosPromise,
}: {
  studiosPromise: Promise<Studio[]>
}) {
  const searchParams = useSearchParams()
  const router = useRouter()
  const pathname = usePathname()
  const studios = use(studiosPromise)
  const selectedStudio = useSelectedStudioStore((state) => state.selectedStudio)
  const { setSelectedStudio, setNoStudios } = useSelectedStudioStoreActions()
  const [isOpen, setIsOpen] = useState(false)

  const handleSelectStudio = useCallback(
    (newStudioId: string) => {
      const studio = studios.find(({ id }) => id === newStudioId)
      if (!studio) {
        return
      }

      setSelectedStudio(studio)
      setIsOpen(false)
      router.push(`${pathname}?studioId=${newStudioId}`)
    },
    [studios, setSelectedStudio, router, pathname]
  )

  useEffect(() => {
    if (studios.length === 0) {
      setNoStudios(true)
    }
  }, [studios, setNoStudios])

  useEffect(() => {
    const studioId = searchParams.get('studioId')
    if (studioId && !selectedStudio) {
      handleSelectStudio(studioId)
    }
  }, [searchParams, selectedStudio, handleSelectStudio])

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={isOpen}
          className="w-3xs justify-between border-primary bg-primary text-sidebar-accent-foreground hover:border-primary hover:bg-primary"
        >
          <LayoutGrid />
          <span className="truncate">
            {selectedStudio
              ? formatStudioDisplayName(
                  selectedStudio.license_number,
                  selectedStudio.name
                )
              : 'Select a studio...'}
          </span>
          <ChevronsUpDown />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-sm p-0">
        <Command>
          <CommandInput placeholder="Search" className="h-9" />
          <CommandList>
            <CommandEmpty>No item found.</CommandEmpty>
            <CommandGroup>
              {studios.map(({ id, name, license_number }) => (
                <CommandItem
                  key={id}
                  value={formatStudioDisplayName(license_number, name)}
                  onSelect={() => handleSelectStudio(id)}
                >
                  {formatStudioDisplayName(license_number, name)}
                  <Check
                    className={cn(
                      'ml-auto',
                      selectedStudio?.id === id ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
